import { ref, remove, set, update } from "firebase/database";
import { db } from "../firebaseConfig";
import { fetchAndCacheWaypoints } from "./waypointsCache.js";
import { uploadImageToServer } from "./uploadToServer.js";
import {
  isWaypointOfflineImageRef,
  loadWaypointOfflineImageFile,
  removeWaypointOfflineImagesFromPayload,
} from "./waypointOfflineImages.js";

const WAYPOINTS_OPS_QUEUE_KEY = "trekkin_waypoints_ops_queue_v1";
const WAYPOINTS_OPS_EVENT = "trekkin:waypoints-ops-queue-updated";

function isOnlineNow() {
  try {
    return typeof navigator === "undefined" ? true : navigator.onLine !== false;
  } catch {
    return true;
  }
}

function normalizeQueue(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      opId: String(item?.opId || "").trim(),
      type: String(item?.type || "").trim(),
      waypointId: String(item?.waypointId || "").trim(),
      payload:
        item?.payload && typeof item.payload === "object" ? { ...item.payload } : null,
      createdAt: Number(item?.createdAt || 0),
      updatedAt: Number(item?.updatedAt || 0),
    }))
    .filter((item) => item.opId && item.type && item.waypointId);
}

function readQueue() {
  try {
    const raw = localStorage.getItem(WAYPOINTS_OPS_QUEUE_KEY);
    if (!raw) return [];
    return normalizeQueue(JSON.parse(raw));
  } catch {
    return [];
  }
}

function dispatchQueueUpdate(queue) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(WAYPOINTS_OPS_EVENT, {
        detail: { queue: Array.isArray(queue) ? queue : [] },
      })
    );
  } catch {
    // ignore
  }
}

function writeQueue(queue) {
  const normalized = normalizeQueue(queue);
  try {
    if (!normalized.length) {
      localStorage.removeItem(WAYPOINTS_OPS_QUEUE_KEY);
    } else {
      localStorage.setItem(WAYPOINTS_OPS_QUEUE_KEY, JSON.stringify(normalized));
    }
  } catch {
    // ignore
  }
  dispatchQueueUpdate(normalized);
  return normalized;
}

function makeOpId(prefix = "wp-op") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function makeTempWaypointId() {
  return `wp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function isTempWaypointId(id) {
  return String(id || "").startsWith("wp-");
}

function buildCreateFingerprint(payload = {}) {
  const name = String(payload?.name || "").trim().toLowerCase();
  const lat = Number(payload?.lat);
  const lon = Number(payload?.lon);
  const desc = String(payload?.desc || "").trim().toLowerCase();
  const createdBy = String(payload?.createdBy || "").trim();
  const latKey = Number.isFinite(lat) ? lat.toFixed(6) : "x";
  const lonKey = Number.isFinite(lon) ? lon.toFixed(6) : "x";
  return `${name}|${latKey}|${lonKey}|${desc}|${createdBy}`;
}

function buildCreatePayload(payload) {
  return {
    name: String(payload?.name || "").trim(),
    lat: Number(payload?.lat),
    lon: Number(payload?.lon),
    desc: String(payload?.desc || "").trim(),
    ele: Number.isFinite(Number(payload?.ele)) ? Number(payload.ele) : null,
    tel: payload?.tel ? String(payload.tel).trim() : null,
    email: payload?.email ? String(payload.email).trim() : null,
    www: payload?.www ? String(payload.www).trim() : null,
    icon: payload?.icon ? String(payload.icon).trim() : null,
    imageUrl: payload?.imageUrl || null,
    coverImage: payload?.coverImage || payload?.imageUrl || null,
    galleryImages: Array.isArray(payload?.galleryImages) ? payload.galleryImages.filter(Boolean) : [],
    youtubeUrl: payload?.youtubeUrl ? String(payload.youtubeUrl).trim() : null,
    visibleOnMap: !!payload?.visibleOnMap,
    visibilityScope:
      String(payload?.visibilityScope || "public") === "private" ? "private" : "public",
    featuredProposal: !!payload?.featuredProposal,
    accommodationPartner: !!payload?.accommodationPartner,
    assistancePartner: !!payload?.assistancePartner,
    rentalPartner: !!payload?.rentalPartner,
    accommodationRentalEnabled: !!payload?.accommodationRentalEnabled,
    rentalPartnerUid: payload?.rentalPartnerUid || null,
    partnerUserUid: payload?.partnerUserUid || null,
    partnerVisible: payload?.partnerVisible === true,
    partnerContentInProgress: payload?.partnerContentInProgress === true,
    partnerAppDiscountPercent: Number.isFinite(Number(payload?.partnerAppDiscountPercent))
      ? Number(payload.partnerAppDiscountPercent)
      : null,
    partnerChargeStripeFee: payload?.partnerChargeStripeFee === true,
    status: payload?.status || "published",
    categoryId: payload?.categoryId || null,
    categoryName: payload?.categoryName || null,
    createdAt: Number(payload?.createdAt || Date.now()),
    createdBy: payload?.createdBy || null,
    createdByName: payload?.createdByName || null,
    source: payload?.source || "manual",
    sourceFile: payload?.sourceFile || null,
  };
}

function buildPatchPayload(patch = {}) {
  const next = {};
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (value === undefined) return;
    next[key] = value;
  });
  return next;
}

function isLocalImageReference(value) {
  const str = String(value || "").trim();
  if (!str) return false;
  return (
    str.startsWith("data:image/") ||
    isWaypointOfflineImageRef(str) ||
    str.startsWith("blob:") ||
    str.startsWith("file:") ||
    str.startsWith("capacitor://localhost/_capacitor_file_")
  );
}

function dataUrlToFile(dataUrl, namePrefix = "waypoint_offline") {
  const raw = String(dataUrl || "").trim();
  if (!raw.startsWith("data:image/")) {
    throw new Error("Formato de imagem offline inválido.");
  }
  const [meta, base64] = raw.split(",", 2);
  if (!meta || !base64) throw new Error("Imagem offline inválida.");
  const mimeMatch = meta.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64$/);
  const mime = mimeMatch?.[1] || "image/jpeg";
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
  const ext = mime.includes("png") ? "png" : mime.includes("webp") ? "webp" : "jpg";
  return new File([bytes], `${namePrefix}_${Date.now()}.${ext}`, { type: mime });
}

async function resolveImageForPayload(payload = {}, waypointId = "") {
  const cache = new Map();
  const resolveOne = async (value, suffix) => {
    const imageUrl = String(value || "").trim();
    if (!isLocalImageReference(imageUrl)) return imageUrl || null;
    if (isWaypointOfflineImageRef(imageUrl)) {
      if (cache.has(imageUrl)) return cache.get(imageUrl);
      const storedFile = await loadWaypointOfflineImageFile(imageUrl);
      if (!storedFile) {
        throw new Error(`Imagem offline do waypoint '${waypointId || "desconhecido"}' não foi encontrada.`);
      }
      const uploadedUrl = await uploadImageToServer(storedFile);
      cache.set(imageUrl, uploadedUrl || null);
      return uploadedUrl || null;
    }
    if (!imageUrl.startsWith("data:image/")) {
      throw new Error(`Imagem offline do waypoint '${waypointId || "desconhecido"}' não pode ser sincronizada (formato não suportado).`);
    }
    if (cache.has(imageUrl)) return cache.get(imageUrl);
    const uploadedUrl = await uploadImageToServer(dataUrlToFile(imageUrl, `waypoint_${waypointId || "sync"}_${suffix}`));
    cache.set(imageUrl, uploadedUrl || null);
    return uploadedUrl || null;
  };
  const gallery = Array.isArray(payload?.galleryImages) ? payload.galleryImages : [];
  const galleryImages = (await Promise.all(gallery.map((item, index) => resolveOne(item, `gallery_${index}`)))).filter(Boolean);
  const imageUrl = await resolveOne(payload?.imageUrl, "cover");
  const coverImage = await resolveOne(payload?.coverImage || payload?.imageUrl, "cover");
  return { ...payload, imageUrl, coverImage, galleryImages };
}

function isLikelyOfflineError(err) {
  if (!err) return !isOnlineNow();
  const message = String(err?.message || err || "").toLowerCase();
  return (
    !isOnlineNow() ||
    message.includes("network") ||
    message.includes("offline") ||
    message.includes("failed to fetch") ||
    message.includes("unavailable") ||
    message.includes("connection") ||
    message.includes("disconnect")
  );
}

export function loadWaypointOpsQueue() {
  return readQueue();
}

export function applyWaypointOpsToList(baseList = [], queue = readQueue()) {
  const map = new Map();
  (Array.isArray(baseList) ? baseList : []).forEach((item) => {
    if (!item?.id) return;
    map.set(String(item.id), { ...item });
  });

  normalizeQueue(queue).forEach((op) => {
    if (op.type === "create") {
      map.set(op.waypointId, {
        id: op.waypointId,
        ...(op.payload || {}),
        pendingSync: true,
        pendingOp: "create",
      });
      return;
    }

    if (op.type === "update") {
      const current = map.get(op.waypointId);
      if (!current) return;
      map.set(op.waypointId, {
        ...current,
        ...(op.payload || {}),
        pendingSync: true,
        pendingOp: current?.pendingOp === "create" ? "create" : "update",
      });
      return;
    }

    if (op.type === "remove") {
      map.delete(op.waypointId);
    }
  });

  return Array.from(map.values()).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export function enqueueCreateWaypoint(payload) {
  const queue = readQueue();
  const normalizedPayload = buildCreatePayload({
    ...payload,
    createdAt: Number(payload?.createdAt || Date.now()),
  });
  const fp = buildCreateFingerprint(normalizedPayload);
  const existing = queue.find(
    (item) =>
      item?.type === "create" &&
      item?.payload &&
      buildCreateFingerprint(item.payload) === fp
  );
  if (existing?.waypointId) {
    existing.payload = { ...(existing.payload || {}), ...normalizedPayload };
    existing.updatedAt = Date.now();
    writeQueue(queue);
    return { tempId: existing.waypointId, queue };
  }
  const tempId = makeTempWaypointId();
  const now = Date.now();
  queue.push({
    opId: makeOpId("wp-create"),
    type: "create",
    waypointId: tempId,
    payload: normalizedPayload,
    createdAt: now,
    updatedAt: now,
  });
  writeQueue(queue);
  return { tempId, queue };
}

export function enqueueImportWaypoints(items = []) {
  const out = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    const created = enqueueCreateWaypoint(item);
    out.push(created.tempId);
  });
  return out;
}

export function enqueueUpdateWaypoint(waypointId, patch) {
  const id = String(waypointId || "").trim();
  if (!id) return { mergedInto: null };
  const nextPatch = buildPatchPayload(patch);
  if (!Object.keys(nextPatch).length) return { mergedInto: id };

  const queue = readQueue();
  const now = Date.now();

  if (isTempWaypointId(id)) {
    const createOp = queue.find((item) => item.type === "create" && item.waypointId === id);
    if (createOp) {
      createOp.payload = {
        ...(createOp.payload || {}),
        ...nextPatch,
      };
      createOp.updatedAt = now;
      writeQueue(queue);
      return { mergedInto: id };
    }
  }

  const existing = queue.find((item) => item.type === "update" && item.waypointId === id);
  if (existing) {
    existing.payload = {
      ...(existing.payload || {}),
      ...nextPatch,
    };
    existing.updatedAt = now;
  } else {
    queue.push({
      opId: makeOpId("wp-update"),
      type: "update",
      waypointId: id,
      payload: nextPatch,
      createdAt: now,
      updatedAt: now,
    });
  }

  writeQueue(queue);
  return { mergedInto: id };
}

export function enqueueRemoveWaypoint(waypointId) {
  const id = String(waypointId || "").trim();
  if (!id) return { removed: false };

  const queuedForWaypoint = readQueue().filter((item) => item.waypointId === id);
  queuedForWaypoint.forEach((item) => {
    void removeWaypointOfflineImagesFromPayload(item?.payload || {}).catch(() => {});
  });
  let queue = readQueue().filter((item) => item.waypointId !== id);
  if (!isTempWaypointId(id)) {
    queue.push({
      opId: makeOpId("wp-remove"),
      type: "remove",
      waypointId: id,
      payload: null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }
  writeQueue(queue);
  return { removed: true };
}

export async function flushWaypointOpsQueue() {
  if (!isOnlineNow()) {
    return { ok: false, reason: "offline", applied: 0, pending: readQueue().length };
  }

  let queue = readQueue();
  let applied = 0;

  while (queue.length) {
    const op = queue[0];
    try {
      if (op.type === "create") {
        const resolvedPayload = await resolveImageForPayload(op.payload || {}, op.waypointId);
        await set(
          ref(db, `waypoints/${op.waypointId}`),
          {
            id: op.waypointId,
            ...buildCreatePayload(resolvedPayload),
            updatedAt: Date.now(),
          }
        );
      } else if (op.type === "update") {
        const resolvedPayload = await resolveImageForPayload(op.payload || {}, op.waypointId);
        await update(
          ref(db, `waypoints/${op.waypointId}`),
          buildPatchPayload(resolvedPayload)
        );
      } else if (op.type === "remove") {
        await remove(ref(db, `waypoints/${op.waypointId}`));
      }
      await removeWaypointOfflineImagesFromPayload(op.payload || {}).catch(() => {});
      queue = queue.slice(1);
      writeQueue(queue);
      applied += 1;
    } catch (err) {
      if (isLikelyOfflineError(err)) {
        return {
          ok: false,
          reason: "offline_during_flush",
          applied,
          pending: queue.length,
          err: err?.message || String(err),
        };
      }
      return {
        ok: false,
        reason: "flush_error",
        applied,
        pending: queue.length,
        err: err?.message || String(err),
      };
    }
  }

  if (applied > 0) {
    try {
      await fetchAndCacheWaypoints({ force: true });
    } catch {
      // ignore cache refresh failures
    }
    if (typeof window !== "undefined") {
      try {
        window.dispatchEvent(new CustomEvent("trekkin:waypoints-refresh"));
      } catch {
        // ignore
      }
    }
  }

  return { ok: true, applied, pending: 0 };
}

export function shouldQueueWaypointWrite(err) {
  return isLikelyOfflineError(err);
}

let autoFlushInitialized = false;
let autoFlushRunning = false;

export function initWaypointOpsAutoFlush() {
  if (autoFlushInitialized || typeof window === "undefined") return;
  autoFlushInitialized = true;

  const run = async () => {
    if (autoFlushRunning) return;
    if (!isOnlineNow()) return;
    autoFlushRunning = true;
    try {
      await flushWaypointOpsQueue();
    } catch {
      // ignore: queue is retried on next online/visibility signal
    } finally {
      autoFlushRunning = false;
    }
  };

  window.addEventListener("online", run);
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });
  window.addEventListener(WAYPOINTS_OPS_EVENT, run);

  // boot attempt
  window.setTimeout(run, 1500);
}

export { WAYPOINTS_OPS_EVENT, WAYPOINTS_OPS_QUEUE_KEY };
